/**
 * Fleet-health flag dispatcher — consume side of the platform fleet-health signal.
 *
 * A fleet-managed mount is bound live by the host fleet rclone, so it is excluded
 * from the boot-time no-phantom-workspace barrier. The in-container manager runs a
 * local `stat`/`readdir` probe that catches directly-detectable failures (EIO /
 * dead bind), but a *silently-empty-but-writable* degraded fleet mount is only
 * visible to the host, which knows `core/stats.errors`.
 *
 * The platform fleet-health poll writes its verdict for connector `<id>` into
 * `<workspace>/.skaile/fleet-health/<id>` (body: `healthy` | `degraded` |
 * `error`). The runner watches that directory and forwards each verdict to the
 * ConnectorManager's `reportFleetHealth`, which trips/lifts the read-only gate
 * with hysteresis.
 *
 * Contract:
 *   - dir:  `<workspace>/.skaile/fleet-health/`
 *   - file: one per connectorId; basename === connectorId.
 *   - body: trimmed, lowercased; `healthy` → healthy, `degraded` → degraded
 *           (live + readable but writes failing ⇒ read-only-readable surface),
 *           anything else (`error` | unreadable | empty) → error (fail-closed).
 *
 * Extracted so the watcher logic can be unit-tested without the WebSocket server.
 */

import { readFile } from "node:fs/promises";
import { join } from "node:path";
import type { FleetHealthVerdict } from "@skaile/workspaces/connectors";
import type { Logger } from "@skaile/workspaces/types";

/** Minimal ConnectorManager surface the dispatcher needs (structural for tests). */
export interface FleetHealthReporter {
  reportFleetHealth(connectorId: string, verdict: FleetHealthVerdict): Promise<void>;
}

/** Map a flag-file body to a verdict. Fail-closed: unknown/empty ⇒ `error`. */
export function parseFleetHealthBody(body: string): FleetHealthVerdict {
  const trimmed = body.trim().toLowerCase();
  if (trimmed === "healthy") return "healthy";
  // `degraded` = live + readable but writes failing → read-only-readable, not a
  // full mask. Distinct from `error` so visible content is not wrongly hidden.
  if (trimmed === "degraded") return "degraded";
  return "error";
}

/**
 * Read the verdict for one changed flag file and forward it to the manager.
 *
 * @param dir - the fleet-health flag directory.
 * @param fileName - the changed entry's basename (=== connectorId).
 * @param manager - the active connector manager, or `null` to short-circuit.
 */
export async function dispatchFleetHealthFlag(
  dir: string,
  fileName: string,
  manager: FleetHealthReporter | null,
  log: Logger,
): Promise<void> {
  if (!manager) return;
  // fs.watch already yields a basename (entry name within the watched dir), so
  // no further basename() normalisation is needed.
  const connectorId = fileName;
  if (!connectorId || connectorId.startsWith(".")) return;
  let verdict: FleetHealthVerdict;
  try {
    verdict = parseFleetHealthBody(await readFile(join(dir, connectorId), "utf8"));
  } catch {
    // Unreadable flag — fail-closed to `error` so a vanished/locked file does not
    // silently leave a possibly-unhealthy mount writable.
    verdict = "error";
  }
  try {
    await manager.reportFleetHealth(connectorId, verdict);
    log.debug("fleet-health flag dispatched", { connectorId, verdict });
  } catch (err) {
    log.warn("fleet-health flag dispatch failed", {
      connectorId,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}
